'use client'

import { Container, Flex, Grid } from '@quark/layout'
import React from 'react'
import { Text } from '@quark/typography'
import { HeroProps } from '@interfaces/home'
import { Animations, MultiReveal, Reveal } from '@quark/animation'
import { NewHomePageComponent } from '@components/HomePage/utils/HomeComponentType'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { getGridFormatsAndOrders } from '@utils/gridLayout'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { getUrlWithQueryParams } from '@utils/queryParamUtils'
import HeroCard from './HeroCards'
import Award from './Award'
import styles from './Hero.module.scss'

const Hero = React.memo((data: HeroProps) => {
  const handleCardClick = async (
    eventProperties: {
      eventName?: string
      customProperties?: Record<string, any>
    },
    mobileUrl: string,
    url: string,
    urlInNewTab: boolean
  ) => {
    await trackEvent(eventProperties?.eventName ?? 'btn_explore_insurance', {
      experiment_name: 'wrapper_v3',
      page_type: 'acko_home_page',
      redirection_url: data?.isMobile ? mobileUrl : url,
      ...eventProperties?.customProperties,
    })
    window.open(getUrlWithQueryParams(data?.isMobile ? mobileUrl : url), urlInNewTab ? '_blank' : '_self')
  }
  const { desktopLayout, tabletLayout, mobileLayout, desktopPosition, tabletPosition, mobilePosition } =
    getGridFormatsAndOrders({
      desktopGridLayout: data?.ackoProductsDesktopGrid?.layout,
      tabletGridLayout: data?.ackoProductsTabletGrid?.layout,
      mobileGridLayout: data?.ackoProductsMobileGrid?.layout,
    })
  return (
    <Container
      id={NewHomePageComponent.Hero}
      type="root"
      backgroundColor="base/primary"
      px={{
        lg: 'spacing-80',
        md: 'spacing-32',
        sm: 'spacing-16',
      }}
      paddingTop={{
        lg: 'spacing-144',
        md: 'spacing-112',
        sm: 'spacing-104',
      }}
      paddingBottom={{
        lg: 'spacing-72',
        md: 'spacing-56',
        sm: 'spacing-40',
      }}>
      <Flex
        alignItems="center"
        flexDirection="column"
        gap={{
          lg: 'spacing-48',
          md: 'spacing-48',
          sm: 'spacing-32',
        }}>
        <Flex
          size="fullWidth"
          flexDirection={{ lg: 'row', md: 'column', sm: 'column' }}
          gap="spacing-16"
          justifyContent="space-between"
          alignItems="center">
          <Reveal as="span" key="home-title" animationProps={{ threshold: 0.1, animationDelayMs: 200 }}>
            <Text
              as="h1"
              display={{ lg: 'block', md: 'block', sm: data?.mobileTitle ? 'none' : 'block' }}
              textAlign={{ lg: 'left', md: 'center', sm: 'center' }}
              fontStyle={{ lg: 'um_text-5xl/bold', md: 'um_text-4xl/bold', sm: 'um_text-2xl/bold' }}
              whiteSpace="pre"
              color="base/primary">
              {data?.title}
            </Text>
            <PropertyControlledComponent controllerProperty={!!data?.mobileTitle} fallback={null}>
              <Text
                as="h1"
                display={{ lg: 'none', md: 'none', sm: 'block' }}
                textAlign={{ lg: 'left', md: 'center', sm: 'center' }}
                fontStyle={{ lg: 'um_text-5xl/bold', md: 'um_text-4xl/bold', sm: 'um_text-2xl/bold' }}
                whiteSpace="pre"
                color="base/primary">
                {data?.mobileTitle}
              </Text>
            </PropertyControlledComponent>
          </Reveal>
          <MultiReveal as={Flex} gap="spacing-16" animationDelays={(i: number) => 500 + (i + 1) * 100}>
            {data?.awards?.map((award, i) => (
              <Award key={i}>{award?.title}</Award>
            ))}
          </MultiReveal>
        </Flex>
        <Flex flexDirection="column" gap={{ lg: 'spacing-16', md: 'spacing-16', sm: 'spacing-12' }} size="fullWidth">
          <MultiReveal
            as={Grid}
            size="fullSize"
            display="grid"
            gridTemplateColumns="auto-fit"
            gridTemplateRows="auto-fill"
            placeItems="stretch"
            gap={{
              lg: 'spacing-16',
              md: 'spacing-12',
              sm: 'spacing-8',
            }}>
            {data?.ackoProducts?.map((cardData, i) => (
              <div
                key={i}
                className={styles.HeroGridItem}
                style={{
                  ['--grid-column-start-lg' as any]: desktopLayout[i].gridColumnStart,
                  ['--grid-column-end-lg' as any]: desktopLayout[i].gridColumnEnd,
                  ['--grid-row-start-lg' as any]: desktopLayout[i].gridRowStart,
                  ['--grid-row-end-lg' as any]: desktopLayout[i].gridRowEnd,

                  ['--grid-column-start-md' as any]: tabletLayout[i].gridColumnStart,
                  ['--grid-column-end-md' as any]: tabletLayout[i].gridColumnEnd,
                  ['--grid-row-start-md' as any]: tabletLayout[i].gridRowStart,
                  ['--grid-row-end-md' as any]: tabletLayout[i].gridRowEnd,

                  ['--grid-column-start-sm' as any]: mobileLayout[i].gridColumnStart,
                  ['--grid-column-end-sm' as any]: mobileLayout[i].gridColumnEnd,
                  ['--grid-row-start-sm' as any]: mobileLayout[i].gridRowStart,
                  ['--grid-row-end-sm' as any]: mobileLayout[i].gridRowEnd,

                  ['--animation-delay-lg' as any]: 300 + (desktopPosition[i] + 1) * 100,
                  ['--animation-delay-md' as any]: 300 + (tabletPosition[i] + 1) * 100,
                  ['--animation-delay-sm' as any]: 300 + (mobilePosition[i] + 1) * 100,
                }}>
                <HeroCard key={i} card={cardData} onClick={handleCardClick} />
              </div>
            ))}
          </MultiReveal>
          <PropertyControlledComponent controllerProperty={!!data?.productsDisclaimer} fallback={null}>
            <Reveal as="span" key="products-disclaimer" animationProps={{ animation: Animations.PopOut }}>
              <Text
                as="p"
                fontStyle={{ lg: 'um_text-xs/normal', md: 'um_text-xs/normal', sm: 'um_text-2xs/normal' }}
                textAlign="center"
                color="base/secondary">
                {data?.productsDisclaimer}
              </Text>
            </Reveal>
          </PropertyControlledComponent>
        </Flex>
      </Flex>
    </Container>
  )
})

export default Hero
